/**
 * Release 26 (owner, 2026-10-30): SATU moves from simu.katedralagats.or.id to
 * satu.katedralagats.or.id. Both names serve the same Netlify site, but a device's offline data
 * (the OPFS database, the outbox, the session, the PIN, the settings) belongs to the origin it
 * was written on: on the new address a device starts empty, signs in again and downloads its
 * data. A change not yet sent from the old origin would be lost there, so no device leaves the
 * old address while its outbox holds anything (shell/PitaPindahAlamat).
 *
 * The two host names are build values, `VITE_ALAMAT_LAMA` and `VITE_ALAMAT_BARU`, defaulting to
 * the owner's decision, so production changes only `VITE_PUBLIC_ORIGIN`; the e2e build points
 * the old one at `localhost` (e2e/build.mjs). The new name may carry a port (e2e only). No
 * imports: ./pindahAwal reads this before anything else of the app is evaluated.
 */

/** What these helpers need of `localStorage`; null where it is missing or blocked. */
export interface PenyimpananAlamat {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** The parts of `window.location` a redirect keeps. */
export interface LokasiAlamat {
  protocol: string
  hostname: string
  pathname: string
  search: string
  hash: string
}

// The old address is SIMU's own name (legacy, kept until the cleanup release drops the move).
export const ALAMAT_LAMA_BAWAAN = 'simu.katedralagats.or.id'
export const ALAMAT_BARU_BAWAAN = 'satu.katedralagats.or.id'

function namaHost(nilai: unknown, bawaan: string): string {
  return typeof nilai === 'string' && nilai.trim() !== '' ? nilai.trim().toLowerCase() : bawaan
}

export const ALAMAT_LAMA = namaHost(import.meta.env.VITE_ALAMAT_LAMA, ALAMAT_LAMA_BAWAAN)
export const ALAMAT_BARU = namaHost(import.meta.env.VITE_ALAMAT_BARU, ALAMAT_BARU_BAWAAN)

/**
 * Set on the old origin when this device moved (release 26). A legacy-style SIMU name on
 * purpose: it lives beside SIMU's other keys on the old origin and goes with them at the
 * cleanup release (pinned: test/nama-warisan.test.ts).
 */
export const KUNCI_PINDAH = 'simu:pindah-alamat'

/** Set on the new origin when Masuk opened with `?dari=alamat-lama`: the reinstall note waits. */
export const KUNCI_DARI_ALAMAT_LAMA = 'satu:dari-alamat-lama'

/** The query Masuk reads on the new address (routes/masuk/MasukRoute). */
export const DARI_ALAMAT_LAMA = 'alamat-lama'

export function diAlamatLama(hostname: string, lama: string = ALAMAT_LAMA): boolean {
  return hostname.toLowerCase() === lama
}

/** The same page on the new address: path, query and fragment kept. */
export function urlAlamatBaru(lokasi: LokasiAlamat, baru: string = ALAMAT_BARU): string {
  return `${lokasi.protocol}//${baru}${lokasi.pathname}${lokasi.search}${lokasi.hash}`
}

/** Where "Pindah sekarang" goes: Masuk on the new address, which then says why. */
export function urlPindah(protocol: string, baru: string = ALAMAT_BARU): string {
  return `${protocol}//${baru}/masuk?dari=${DARI_ALAMAT_LAMA}`
}

/** The moved band's link: the new address's start, which opens Beranda or Masuk as it should. */
export function bukaAlamatBaru(protocol: string, baru: string = ALAMAT_BARU): string {
  return `${protocol}//${baru}/`
}

function baca(storage: PenyimpananAlamat | null, kunci: string): string | null | undefined {
  if (storage === null) return undefined
  try {
    return storage.getItem(kunci)
  } catch {
    return undefined
  }
}

/**
 * Does this device hold SATU data on this origin? An open or locked session (the launch hint,
 * main.tsx), or the parish name a session's shell cached (store/sesi; it stays after Keluar,
 * as the database and its outbox do). Both are SIMU's legacy names. The device database alone
 * is no sign: the sign-in page opens an empty one on the first visit. Storage that cannot be
 * read counts as data, so no device is ever sent away on a guess.
 */
export function adaDataPerangkat(storage: PenyimpananAlamat | null): boolean {
  const petunjuk = baca(storage, 'simu:sesi-petunjuk')
  if (petunjuk === undefined) return true
  if (petunjuk === 'aktif' || petunjuk === 'terkunci') return true
  const paroki = baca(storage, 'simu:paroki')
  return paroki === undefined || paroki !== null
}

export function sudahPindah(storage: PenyimpananAlamat | null): boolean {
  const nilai = baca(storage, KUNCI_PINDAH)
  return nilai !== undefined && nilai !== null
}

/** Records the move; false when the storage refuses it (then the device does not move). */
export function tandaiPindah(storage: PenyimpananAlamat | null, pada: string): boolean {
  if (storage === null) return false
  try {
    storage.setItem(KUNCI_PINDAH, JSON.stringify({ ke: ALAMAT_BARU, pada }))
    return true
  } catch {
    return false
  }
}

/** The final sync left something unsent: the device stays and works on. */
export function batalPindah(storage: PenyimpananAlamat | null): void {
  try {
    storage?.removeItem(KUNCI_PINDAH)
  } catch {
    // Nothing more to do: a marker that cannot be removed was never written.
  }
}

/**
 * Where a launch goes before the app opens anything: on the old address, a device with no SATU
 * data and not marked as moved goes to the same page on the new one; every other launch stays.
 */
export function arahAwal(lokasi: LokasiAlamat, storage: PenyimpananAlamat | null): string | null {
  if (!diAlamatLama(lokasi.hostname)) return null
  if (sudahPindah(storage) || adaDataPerangkat(storage)) return null
  return urlAlamatBaru(lokasi)
}

/** `localStorage`, or null where it is missing or blocked. */
export function penyimpananPeramban(): PenyimpananAlamat | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}
